// src/components/resources/glossary/HowAdSightsHelpsCard.tsx

import Link from 'next/link'

import { RequestAccessLink } from '~/components/resources/RequestAccessLink'
import { siteConfig } from '~/lib/config/site'

interface HowAdSightsHelpsCardProps {
  /** The metric or term name to interpolate into the heading. */
  termName: string
  /** The product-tie-in paragraph copy. Renders nothing if empty. */
  copy: string | undefined
  /**
   * Required id prefix (typically the term slug) so the heading id is
   * unique per render. Without this, multiple instances on one page
   * collide on `id="how-adsights-helps"` and break aria-labelledby
   * resolution.
   */
  idPrefix: string
  /**
   * Framing variant. Absent (default) keeps the analytics framing —
   * "How AdSights helps you track X" + the `/request-access` CTA.
   * `'framework'` renders the storefront-funnel framing — "How AdSights
   * helps with X" + an Ads Framework CTA — for terms whose copy describes
   * the production pipeline rather than metric tracking (`r3566303320`:
   * the tracking heading + request-access CTA contradicted framework
   * copy and routed readers away from the funnel the term targets).
   */
  variant?: 'framework'
  /** Optional CSS class override for the outer section. */
  className?: string
}

/**
 * Shared server component for the "How AdSights helps…" callout. Used by
 * both the SSR fallback (GlossaryTermServerContent) and the client-enhanced
 * layout (GlossaryTermLayout) so the two render paths can't drift in styling.
 *
 * Two framings share one card shell:
 * - default: metric-tracking copy, "…helps you track X", RequestAccessLink
 * - `variant="framework"`: production-pipeline copy, "…helps with X",
 *   crawlable CTA to `/frameworks/ads-framework`
 *
 * Returns null when the copy is empty so callers can render unconditionally.
 */
export function HowAdSightsHelpsCard({
  termName,
  copy,
  idPrefix,
  variant,
  className = ''
}: HowAdSightsHelpsCardProps): JSX.Element | null {
  // Whitespace-only copy (e.g. `howAdSightsHelps: " "` in YAML) would
  // satisfy `if (!copy)` and render an empty indigo callout below the
  // metric content. Trim before the truthiness check so author typos
  // that leave only whitespace collapse to "no callout" rather than a
  // blank card.
  const trimmed = copy?.trim()
  if (!trimmed) return null
  const headingId = `${idPrefix}-how-adsights-helps`
  const isFramework = variant === 'framework'
  return (
    <section className={`mb-8 ${className}`.trim()} aria-labelledby={headingId}>
      <h2 id={headingId} className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
        {isFramework ? <>How AdSights helps with {termName}</> : <>How AdSights helps you track {termName}</>}
      </h2>
      <div className="bg-indigo-50 dark:bg-indigo-900/10 p-6 rounded-lg border border-indigo-100 dark:border-indigo-900/40">
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{trimmed}</p>
        {/* Contextual funnel — a real, crawlable internal link from this
            high-intent glossary page to the conversion surface the copy
            actually describes: the Ads Framework storefront for
            production-pipeline (framework-variant) terms, or Request
            Access for the analytics platform (default). */}
        <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-indigo-100 pt-4 dark:border-indigo-900/40">
          {isFramework ? (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Want this workflow for your own ad production?
              </p>
              <Link
                href={siteConfig.baseLinks.adsFramework}
                className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:text-indigo-600 dark:text-indigo-300 dark:hover:text-indigo-200"
              >
                Explore the Ads Framework
                <span aria-hidden="true">→</span>
              </Link>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Want AI to track {termName} across your creative automatically?
              </p>
              <RequestAccessLink />
            </>
          )}
        </div>
      </div>
    </section>
  )
}

export default HowAdSightsHelpsCard
